import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, X, Users, Check } from "lucide-react";
import { useSeason } from "@/context/SeasonContext";
import { useSeasonCategories } from "@/hooks/useSeasonCategories";
import { displayName, initials } from "@/lib/athleteName";

/**
 * AthletePicker — Componente condiviso per ricerca, filtro e selezione atleti.
 *
 * Ordinamento default: cognome A→Z, poi nome A→Z.
 * Ricerca: nome, cognome, "nome cognome", "cognome nome" — case-insensitive, tollerante agli spazi.
 * Filtri visibili di default: Cerca, Categoria, Arma.
 * Filtri collassati in "Altri filtri": Gruppo, Anno di nascita (singolo), Fascia anni, Agonistico/Non, Attivi/Archiviati.
 *
 * Props:
 *  - open, onOpenChange: controllo modal
 *  - title, description
 *  - multiSelect (default true)
 *  - initialSelectedIds (array)
 *  - preset: { categoryCode, weaponCode, groupId } — filtri suggeriti (utente può modificare)
 *  - onConfirm(selectedIds, selectedAthletes)
 *  - confirmLabel
 *  - activeOnly (default true)  → nasconde archiviati
 *  - renderRowExtra(athlete)  → opzionale, mostrato a destra della riga (es. tag)
 */
export default function AthletePicker({
  open,
  onOpenChange,
  title = "Seleziona atleti",
  description = null,
  multiSelect = true,
  initialSelectedIds = [],
  preset = {},
  onConfirm,
  confirmLabel,
  activeOnly = true,
  renderRowExtra = null,
}) {
  const { selectedSeasonId } = useSeason();
  const { categories, catMap, filterByCategory } = useSeasonCategories(selectedSeasonId);

  const [athletes, setAthletes] = useState([]);
  const [weaponsMap, setWeaponsMap] = useState({});
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(new Set(initialSelectedIds));
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(preset.categoryCode || "all");
  const [weaponFilter, setWeaponFilter] = useState(preset.weaponCode || "all");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [groupFilter, setGroupFilter] = useState(preset.groupId || "all");
  const [birthYearFrom, setBirthYearFrom] = useState("");
  const [birthYearTo, setBirthYearTo] = useState("");
  const [competitiveFilter, setCompetitiveFilter] = useState("all"); // all | agonistico | non_agonistico
  const [archivedFilter, setArchivedFilter] = useState(activeOnly ? "active" : "all");

  // Load athletes when opened
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [aRes, gRes, wRes] = await Promise.all([
          api.get(selectedSeasonId ? `/athletes-with-categories?season_id=${selectedSeasonId}` : "/athletes-with-categories"),
          api.get("/groups"),
          api.get("/athletes-with-weapons"),
        ]);
        if (cancelled) return;
        setAthletes(aRes.data?.athletes || []);
        setGroups(gRes.data || []);
        const wm = {};
        (wRes.data || []).forEach((a) => { wm[a.id] = (a.weapons || []).filter((w) => w.status === "attiva" || w.status === "secondaria"); });
        setWeaponsMap(wm);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, selectedSeasonId]);

  // Sync initial selection when opened
  useEffect(() => {
    if (open) setSelected(new Set(initialSelectedIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset preset changes when reopened with different preset
  useEffect(() => {
    if (!open) return;
    setCategoryFilter(preset.categoryCode || "all");
    setWeaponFilter(preset.weaponCode || "all");
    setGroupFilter(preset.groupId || "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset.categoryCode, preset.weaponCode, preset.groupId]);

  const normalize = (s) => (s || "").toString().toLowerCase().replace(/\s+/g, " ").trim();

  const filtered = useMemo(() => {
    let list = athletes;
    // Archived · quando activeOnly gli archiviati NON sono mai selezionabili (regola active-only riusata)
    if (activeOnly) list = list.filter((a) => !a.archived);
    else if (archivedFilter === "active") list = list.filter((a) => !a.archived);
    else if (archivedFilter === "archived") list = list.filter((a) => !!a.archived);

    // Category (via useSeasonCategories helper)
    list = filterByCategory(list, categoryFilter);

    // Weapon (attive/secondarie)
    if (weaponFilter !== "all") {
      list = list.filter((a) => {
        const ws = weaponsMap[a.id] || [];
        return ws.some((w) => w.weapon_code === weaponFilter);
      });
    }

    // Group (multi-gruppo: match su group_ids[] con fallback su group_id legacy)
    if (groupFilter !== "all") {
      list = list.filter((a) => (a.group_ids?.length ? a.group_ids : (a.group_id ? [a.group_id] : [])).includes(groupFilter));
    }

    // Birth year range
    const yFrom = birthYearFrom ? parseInt(birthYearFrom, 10) : null;
    const yTo = birthYearTo ? parseInt(birthYearTo, 10) : null;
    if (yFrom || yTo) {
      list = list.filter((a) => {
        if (!a.birth_date) return false;
        const y = parseInt(String(a.birth_date).slice(0, 4), 10);
        if (Number.isNaN(y)) return false;
        if (yFrom && y < yFrom) return false;
        if (yTo && y > yTo) return false;
        return true;
      });
    }

    // Competitive status (approssimazione: competitive|elite = agonistico)
    if (competitiveFilter === "agonistico") {
      list = list.filter((a) => a.monitoring_level === "competitive" || a.monitoring_level === "elite");
    } else if (competitiveFilter === "non_agonistico") {
      list = list.filter((a) => a.monitoring_level === "standard" || !a.monitoring_level);
    }

    // Search: match on first, last, "first last", "last first"
    const query = normalize(q);
    if (query) {
      list = list.filter((a) => {
        const first = normalize(a.first_name);
        const last = normalize(a.last_name);
        const fl = `${first} ${last}`.trim();
        const lf = `${last} ${first}`.trim();
        return (
          first.includes(query) ||
          last.includes(query) ||
          fl.includes(query) ||
          lf.includes(query)
        );
      });
    }

    // Sort: cognome A→Z, poi nome A→Z
    const sorted = [...list].sort((a, b) => {
      const ln = normalize(a.last_name).localeCompare(normalize(b.last_name), "it");
      if (ln !== 0) return ln;
      return normalize(a.first_name).localeCompare(normalize(b.first_name), "it");
    });
    return sorted;
  }, [athletes, filterByCategory, categoryFilter, weaponFilter, weaponsMap, groupFilter, birthYearFrom, birthYearTo, competitiveFilter, archivedFilter, q]);

  const filteredIds = useMemo(() => filtered.map((a) => a.id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (!multiSelect) {
        return s.has(id) ? new Set() : new Set([id]);
      }
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (allFilteredSelected) {
        for (const id of filteredIds) s.delete(id);
      } else {
        for (const id of filteredIds) s.add(id);
      }
      return s;
    });
  };

  const clearFilters = () => {
    setQ("");
    setCategoryFilter("all");
    setWeaponFilter("all");
    setGroupFilter("all");
    setBirthYearFrom("");
    setBirthYearTo("");
    setCompetitiveFilter("all");
    setArchivedFilter(activeOnly ? "active" : "all");
  };

  const handleConfirm = () => {
    const ids = Array.from(selected);
    const list = athletes.filter((a) => selected.has(a.id));
    onConfirm?.(ids, list);
  };

  const weaponLabel = (a) => {
    const ws = weaponsMap[a.id] || [];
    if (ws.length === 0) return "—";
    const primary = ws.find((w) => w.is_primary) || ws[0];
    if (ws.length === 1) return cap(primary.weapon_code);
    return `${cap(primary.weapon_code)} +${ws.length - 1}`;
  };

  const catLabel = (a) => {
    const cats = catMap[a.id] || [];
    if (cats.length === 0) return "—";
    const p = cats.find((c) => c.is_primary) || cats[0];
    return p.display_name;
  };

  const groupLabel = (a) => {
    const ids = a.group_ids?.length ? a.group_ids : (a.group_id ? [a.group_id] : []);
    if (!ids.length) return "";
    const names = ids.map((id) => groups.find((x) => x.id === id)?.name).filter(Boolean);
    if (names.length === 0) return "";
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  };

  const activeFiltersCount = [
    categoryFilter !== "all",
    weaponFilter !== "all",
    groupFilter !== "all",
    !!birthYearFrom,
    !!birthYearTo,
    competitiveFilter !== "all",
    archivedFilter !== (activeOnly ? "active" : "all"),
  ].filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0" data-testid="athlete-picker-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Base filters */}
        <div className="px-6 pb-2 space-y-2 border-b border-zinc-100">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                data-testid="athlete-picker-search"
                placeholder="Cerca atleta…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 h-10"
                autoFocus
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="athlete-picker-category" className="w-44 h-10"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categories.filter((c) => c.is_active !== false).map((c) => (
                  <SelectItem key={c.id} value={c.code}>
                    {c.male_name === c.female_name ? c.male_name : `${c.male_name} / ${c.female_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={weaponFilter} onValueChange={setWeaponFilter}>
              <SelectTrigger data-testid="athlete-picker-weapon" className="w-36 h-10"><SelectValue placeholder="Arma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le armi</SelectItem>
                <SelectItem value="fioretto">Fioretto</SelectItem>
                <SelectItem value="spada">Spada</SelectItem>
                <SelectItem value="sciabola">Sciabola</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-10"
              data-testid="athlete-picker-toggle-advanced"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
              Altri filtri{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
            </Button>
            {(q || activeFiltersCount > 0) && (
              <Button variant="ghost" size="sm" className="h-10 text-zinc-500" onClick={clearFilters} data-testid="athlete-picker-clear-filters">
                <X className="w-3.5 h-3.5 mr-1" />Reset
              </Button>
            )}
          </div>

          {showAdvanced && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 pt-2 pb-1">
              <Select value={groupFilter} onValueChange={setGroupFilter}>
                <SelectTrigger data-testid="athlete-picker-group" className="h-10"><SelectValue placeholder="Gruppo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i gruppi</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={competitiveFilter} onValueChange={setCompetitiveFilter}>
                <SelectTrigger data-testid="athlete-picker-competitive" className="h-10"><SelectValue placeholder="Livello" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i livelli</SelectItem>
                  <SelectItem value="agonistico">Agonistico</SelectItem>
                  <SelectItem value="non_agonistico">Non agonistico</SelectItem>
                </SelectContent>
              </Select>
              <Select value={activeOnly ? "active" : archivedFilter} onValueChange={setArchivedFilter} disabled={activeOnly}>
                <SelectTrigger data-testid="athlete-picker-archived" className="h-10"><SelectValue placeholder="Stato scheda" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Solo attivi</SelectItem>
                  {!activeOnly && <SelectItem value="archived">Solo archiviati</SelectItem>}
                  {!activeOnly && <SelectItem value="all">Tutti</SelectItem>}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 col-span-2 lg:col-span-3">
                <span className="text-xs uppercase tracking-wider text-zinc-500 whitespace-nowrap">Anno di nascita</span>
                <Input
                  data-testid="athlete-picker-year-from"
                  type="number"
                  placeholder="Da (es. 2013)"
                  value={birthYearFrom}
                  onChange={(e) => setBirthYearFrom(e.target.value)}
                  className="h-10 w-36"
                />
                <span className="text-zinc-400">→</span>
                <Input
                  data-testid="athlete-picker-year-to"
                  type="number"
                  placeholder="A (es. 2016)"
                  value={birthYearTo}
                  onChange={(e) => setBirthYearTo(e.target.value)}
                  className="h-10 w-36"
                />
              </div>
            </div>
          )}
        </div>

        {/* Bulk row */}
        {multiSelect && (
          <div className="px-6 py-2 flex items-center justify-between text-sm border-b border-zinc-100 bg-zinc-50">
            <div className="flex items-center gap-3">
              <button
                data-testid="athlete-picker-select-all"
                onClick={toggleAllFiltered}
                disabled={filteredIds.length === 0}
                className="text-xs uppercase tracking-wider font-medium hover:underline disabled:text-zinc-300 disabled:no-underline"
              >
                {allFilteredSelected ? "Deseleziona tutti i filtrati" : `Seleziona tutti (${filteredIds.length})`}
              </button>
              {selected.size > 0 && (
                <button
                  data-testid="athlete-picker-clear-selection"
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-zinc-500 hover:text-black"
                >Svuota selezione</button>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
              <Users className="w-3.5 h-3.5" />
              <span data-testid="athlete-picker-counter"><strong className="text-black">{selected.size}</strong> selezionati · {filtered.length} visibili</span>
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto min-h-[240px]">
          {loading && <div className="px-6 py-10 text-center text-sm text-zinc-500">Caricamento atleti…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-zinc-500" data-testid="athlete-picker-empty">
              Nessun atleta corrisponde ai filtri.
            </div>
          )}
          {!loading && filtered.map((a) => {
            const isSel = selected.has(a.id);
            return (
              <label
                key={a.id}
                data-testid={`athlete-picker-row-${a.id}`}
                className={`flex items-center gap-3 px-6 py-2.5 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 ${isSel ? "bg-blue-50 hover:bg-blue-50" : ""}`}
              >
                <input
                  type={multiSelect ? "checkbox" : "radio"}
                  checked={isSel}
                  onChange={() => toggleOne(a.id)}
                  data-testid={`athlete-picker-check-${a.id}`}
                  className="w-4 h-4"
                />
                <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-[10px] font-semibold shrink-0">
                  {initials(a)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{displayName(a)}</div>
                  <div className="text-[11px] text-zinc-500 font-mono truncate">
                    {catLabel(a)}
                    <span className="mx-1.5 text-zinc-300">·</span>
                    {weaponLabel(a)}
                    {groupLabel(a) && (<><span className="mx-1.5 text-zinc-300">·</span>{groupLabel(a)}</>)}
                    {a.birth_date && (<><span className="mx-1.5 text-zinc-300">·</span>{String(a.birth_date).slice(0, 4)}</>)}
                  </div>
                </div>
                {renderRowExtra && <div onClick={(e) => e.preventDefault()}>{renderRowExtra(a)}</div>}
              </label>
            );
          })}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-100">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="athlete-picker-cancel">Annulla</Button>
          <Button
            data-testid="athlete-picker-confirm"
            onClick={handleConfirm}
            disabled={selected.size === 0}
            className="bg-black hover:bg-zinc-800"
          >
            <Check className="w-4 h-4 mr-1.5" />
            {confirmLabel || (multiSelect ? `Conferma ${selected.size} selezionati` : "Conferma")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function cap(s) { return (s || "").charAt(0).toUpperCase() + (s || "").slice(1); }
